import React from 'react';

import { Badge } from 'antd';

import { constant, fn, nat, pipe, remote } from '@code-expert/prelude';
import type { CourseId, SemesterId } from '/imports/domain';
import { useCourseInboxCount } from '/imports/ui/components/Messages/hooks';
import { useUserInboxContext } from '/imports/ui/components/Messages/UserInboxContext';
import { ThemeProvider } from '/imports/ui/foundation/Theme';

interface MessageInboxCountProps {
  children?: React.ReactElement<$IntentionalAny>;
  offset?: [number, number];
}

const MessageInboxCount = React.memo(function MessageInboxCount({
  count,
  offset,
  children,
  ...rest
}: MessageInboxCountProps & { count: remote.Remote<nat.Nat> }) {
  // We can't use GuardRemote, see https://codeexpert.myjetbrains.com/youtrack/issue/cx-3700
  const countOrZero = pipe(count, remote.getOrElse(constant(0)));
  return (
    <ThemeProvider
      themeOverride={({ tokens }) => ({
        components: {
          Badge: {
            colorBgContainer: tokens.colorWhite,
            colorError: tokens.colorPrimary,
            colorErrorHover: tokens.colorPrimaryHover,
          },
        },
      })}
    >
      {children != null ? (
        <Badge count={countOrZero} offset={offset}>
          {React.Children.map(children, (child) => React.cloneElement(child, { ...rest }))}
        </Badge>
      ) : (
        <Badge count={countOrZero} offset={offset} />
      )}
    </ThemeProvider>
  );
});

export const UserInboxCount = ({ offset, children }: MessageInboxCountProps) => {
  const { userMessages, userNotifications } = useUserInboxContext();

  const total = pipe(
    remote.sequenceT(userMessages.count, userNotifications.count),
    remote.map(fn.tupled(nat.SumMonoid.concat)),
  );

  return (
    <MessageInboxCount count={total} offset={offset}>
      {children}
    </MessageInboxCount>
  );
};

export const CourseInboxCount = ({
  courseId,
  semester,
  offset,
  children,
}: MessageInboxCountProps & { courseId: CourseId; semester: SemesterId }) => {
  const { count } = useCourseInboxCount({ courseId, semester });
  return (
    <MessageInboxCount count={count} offset={offset}>
      {children}
    </MessageInboxCount>
  );
};
